import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowDown, ArrowRight, Loader2, Sparkles, TrendingDown, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getAcquisitionReport } from "@/lib/acquisition.functions";
import { getGa4Summary } from "@/lib/ga4.functions";
import { classifyChannel } from "@/lib/acquisition-channel";
import type { FunnelRates, FunnelTotals } from "@/lib/acquisition-report";

export const Route = createFileRoute("/_authenticated/admin/acquisition")({
  head: () => ({
    meta: [
      { title: "Acquisition Intelligence | XAU Pro Academy Admin" },
      {
        name: "description",
        content: "Private first-touch acquisition, conversion, and channel-quality reporting for XAU Pro Academy.",
      },
      { property: "og:title", content: "Acquisition Intelligence | XAU Pro Academy Admin" },
      {
        property: "og:description",
        content: "Private first-touch acquisition, conversion, and channel-quality reporting for XAU Pro Academy.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AdminAcquisition,
});

const CHANNEL_LABELS: Record<string, string> = {
  google_organic: "Google Organic",
  google_paid: "Google Paid",
  ai_discovery: "AI Discovery",
  other_search: "Other Search",
  social: "Social",
  email: "Email",
  referral: "Referral",
  direct: "Direct",
};

function pct(n: number, d: number): string {
  if (!d) return "Not comparable";
  return `${((n / d) * 100).toFixed(1)}%`;
}

function ratePct(value: number | null): string {
  return value === null ? "Not comparable" : `${(value * 100).toFixed(1)}%`;
}

function AdminAcquisition() {
  const fetchReport = useServerFn(getAcquisitionReport);
  const [range, setRange] = useState<"7d" | "30d" | "all">("all");
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "acquisition-report", range],
    queryFn: () => fetchReport({ data: { range } }),
    refetchInterval: 60_000,
  });

  return (
    <div className="space-y-8">
      <div>
        <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-gold">
          <TrendingUp className="h-3 w-3" />
          Acquisition
        </div>
        <h1 className="mt-1 font-display text-3xl font-bold">Channel Attribution</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          First-touch attribution. Admin only. Not analytics.
        </p>
        <div className="mt-4 inline-flex rounded-md border border-border/60 bg-card p-0.5">
          {([
            ["7d", "Last 7 days"],
            ["30d", "Last 30 days"],
            ["all", "All time"],
          ] as const).map(([k, label]) => (
            <Button
              key={k}
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setRange(k)}
              className={`h-7 rounded px-3 font-mono text-[10px] uppercase tracking-[0.14em] ${
                range === k ? "bg-gold-muted text-gold" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading report.
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          Failed to load report.
        </div>
      )}

      {data && (
        <>
          <ConversionFunnel totals={data.totals} rates={data.rates} />

          <PeriodComparison current={data.totals} previous={data.previous?.totals ?? null} range={range} />

          <section>
            <div className="mb-3">
              <h2 className="font-display text-lg font-bold">Channel quality</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                First-touch cohorts. Visits include anonymous visitors in the selected period.
              </p>
            </div>
            <div className="overflow-x-auto rounded-xl border border-border/60 bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted/30 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 text-left">Channel</th>
                    <th className="px-4 py-3 text-right">Visits</th>
                    <th className="px-4 py-3 text-right">Signups</th>
                    <th className="px-4 py-3 text-right">Activated</th>
                    <th className="px-4 py-3 text-right">First subs</th>
                    <th className="px-4 py-3 text-right">Active subs</th>
                    <th className="px-4 py-3 text-right">Visit to signup</th>
                    <th className="px-4 py-3 text-right">Signup to activation</th>
                    <th className="px-4 py-3 text-right">Signup to first sub</th>
                  </tr>
                </thead>
                <tbody>
                  {data.channels.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-4 py-6 text-center text-muted-foreground">
                        No visits in this period.
                      </td>
                    </tr>
                  )}
                  {data.channels.map((c) => (
                    <tr key={c.derived_channel} className="border-t border-border/40">
                      <td className="sticky left-0 bg-card px-4 py-3 font-medium">
                        {CHANNEL_LABELS[c.derived_channel] ?? c.derived_channel}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{c.visits}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{c.signups}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{c.activated}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{c.first_subscriptions}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-gold">{c.active_subscribers}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{pct(c.signups, c.visits)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{pct(c.activated, c.signups)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{pct(c.first_subscriptions, c.signups)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="mb-3 font-display text-lg font-bold">Top converting landing pages</h2>
            <p className="mb-3 text-xs text-muted-foreground">
              First page seen per visitor. Ranked by signups, then visits. Top 20.
            </p>
            <div className="overflow-x-auto rounded-xl border border-border/60 bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted/30 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 text-left">Landing page</th>
                    <th className="px-4 py-3 text-right">Visits</th>
                    <th className="px-4 py-3 text-right">Signups</th>
                    <th className="px-4 py-3 text-right">Members</th>
                    <th className="px-4 py-3 text-right">Signup rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.landing_pages.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                        No visits in this range.
                      </td>
                    </tr>
                  )}
                  {data.landing_pages.map((l) => (
                    <tr key={l.path} className="border-t border-border/40">
                      <td className="max-w-[320px] truncate px-4 py-3 font-mono text-[12px]" title={l.path}>{l.path}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{l.visits}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{l.signups}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-gold">{l.members}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{pct(l.signups, l.visits)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {data.ai_sources.length > 0 && (
            <section>
              <div className="mb-3 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-gold" />
                <h2 className="font-display text-lg font-bold">AI Discovery breakdown</h2>
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                Traffic from AI assistants and answer engines. Visits include anonymous
                sessions. Conversion rates are signups divided by visits, and members
                divided by visits.
              </p>
              <div className="overflow-x-auto rounded-xl border border-border/60 bg-card">
                <table className="w-full text-sm">
                  <thead className="bg-muted/30 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3 text-left">Source</th>
                      <th className="px-4 py-3 text-right">Visits</th>
                      <th className="px-4 py-3 text-right">Signups</th>
                      <th className="px-4 py-3 text-right">Members</th>
                      <th className="px-4 py-3 text-right">Signup rate</th>
                      <th className="px-4 py-3 text-right">Paid conversion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ai_sources.map((s) => (
                      <tr key={s.source} className="border-t border-border/40">
                        <td className="px-4 py-3 font-medium">{s.source}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{s.visits}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{s.signups}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-gold">{s.members}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{pct(s.signups, s.visits)}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{pct(s.members, s.visits)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section>
            <h2 className="mb-3 font-display text-lg font-bold">Recent signups (latest 25 in range)</h2>
            <div className="overflow-x-auto rounded-xl border border-border/60 bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted/30 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-left">First seen</th>
                    <th className="px-4 py-2 text-left">User</th>
                    <th className="px-4 py-2 text-left">Channel</th>
                    <th className="px-4 py-2 text-left">Sub-source</th>
                    <th className="px-4 py-2 text-left">Source / Medium / Campaign</th>
                    <th className="px-4 py-2 text-left">Referrer</th>
                    <th className="px-4 py-2 text-left">Landing</th>
                    <th className="px-4 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent.map((r) => (
                    <tr key={r.user_id} className="border-t border-border/40">
                      <td className="px-4 py-2 text-muted-foreground">
                        {new Date(r.first_seen_at).toISOString().slice(0, 16).replace("T", " ")}
                      </td>
                      <td className="px-4 py-2">{r.display_name ?? r.user_id.slice(0, 8)}</td>
                      <td className="px-4 py-2">
                        {CHANNEL_LABELS[r.derived_channel] ?? r.derived_channel}
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">
                        {classifyChannel({
                          utm_source: r.utm_source,
                          utm_medium: r.utm_medium,
                          first_referrer_host: r.first_referrer_host,
                        }).source ?? "-"}
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">
                        {[r.utm_source, r.utm_medium, r.utm_campaign].filter(Boolean).join(" / ") || "-"}
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">{r.first_referrer_host ?? "-"}</td>
                      <td className="px-4 py-2 text-muted-foreground">{r.first_landing_path ?? "-"}</td>
                      <td className="px-4 py-2">
                        {r.current_subscription_status ? (
                          <span className="text-gold">{r.current_subscription_status}</span>
                        ) : r.activated_at ? (
                          <span className="text-emerald-400">activated</span>
                        ) : (
                          <span className="text-muted-foreground">signup</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {data.recent.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-6 text-center text-muted-foreground">
                        No recent signups.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <p className="text-xs text-muted-foreground">
            Channel classification rules version: {data.classification_version_current}.
            Historical rows retain the ruleset that was active at the time of capture.
          </p>
        </>
      )}

      <Ga4Panel range={range} />
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card p-4">
      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 font-display text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

type FunnelStep = {
  label: string;
  value: number;
  previousRate: number | null;
  cumulativeRate: number | null;
};

function ConversionFunnel({ totals, rates }: { totals: FunnelTotals; rates: FunnelRates }) {
  const steps: FunnelStep[] = [
    { label: "Visits", value: totals.visits, previousRate: null, cumulativeRate: null },
    { label: "Signups", value: totals.signups, previousRate: rates.visit_to_signup, cumulativeRate: rates.visit_to_signup },
    { label: "Activated", value: totals.activated, previousRate: rates.signup_to_activation, cumulativeRate: rates.visit_to_activation },
    { label: "First subscriptions", value: totals.first_subscriptions, previousRate: rates.activation_to_subscription, cumulativeRate: rates.visit_to_subscription },
  ];

  return (
    <section aria-labelledby="conversion-funnel-title">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 id="conversion-funnel-title" className="font-display text-lg font-bold">Conversion funnel</h2>
          <p className="mt-1 text-xs text-muted-foreground">First-touch cohort progression from initial visit.</p>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
          Active subscribers <span className="ml-1 text-gold">{totals.active_subscribers}</span>
        </div>
      </div>
      <div className="grid overflow-hidden rounded-xl border border-border/60 bg-card md:grid-cols-4">
        {steps.map((step, index) => (
          <div key={step.label} className="relative border-b border-border/40 p-4 last:border-b-0 md:border-b-0 md:border-r md:last:border-r-0">
            {index > 0 && (
              <div className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase text-muted-foreground md:absolute md:-left-3 md:top-1/2 md:z-10 md:-translate-y-1/2 md:rounded-full md:border md:border-border/60 md:bg-background md:p-1.5">
                <ArrowDown className="h-3 w-3 md:hidden" />
                <ArrowRight className="hidden h-3 w-3 md:block" />
                <span className="md:hidden">{ratePct(step.previousRate)} from prior stage</span>
              </div>
            )}
            <div className="font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{step.label}</div>
            <div className="mt-1 font-display text-3xl font-bold tabular-nums">{step.value}</div>
            <div className="mt-2 space-y-0.5 text-[11px] text-muted-foreground">
              {index > 0 && <div className="hidden md:block">{ratePct(step.previousRate)} from prior stage</div>}
              <div>{index === 0 ? "Cohort baseline" : `${ratePct(step.cumulativeRate)} of visits`}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const COMPARISON_METRICS: Array<{ key: keyof FunnelTotals; label: string }> = [
  { key: "visits", label: "Visits" },
  { key: "signups", label: "Signups" },
  { key: "activated", label: "Activated" },
  { key: "first_subscriptions", label: "First subscriptions" },
];

function PeriodComparison({
  current,
  previous,
  range,
}: {
  current: FunnelTotals;
  previous: FunnelTotals | null;
  range: "7d" | "30d" | "all";
}) {
  if (!previous) {
    return (
      <section className="border-y border-border/40 py-4 text-xs text-muted-foreground">
        Period comparison is available for the 7-day and 30-day views.
      </section>
    );
  }

  const currentSignupRate = current.visits ? current.signups / current.visits : null;
  const previousSignupRate = previous.visits ? previous.signups / previous.visits : null;
  const pointChange = currentSignupRate !== null && previousSignupRate !== null
    ? (currentSignupRate - previousSignupRate) * 100
    : null;

  return (
    <section aria-labelledby="period-comparison-title">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="period-comparison-title" className="font-display text-lg font-bold">Period comparison</h2>
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
          Versus previous {range === "7d" ? "7 days" : "30 days"}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border/60 bg-border/40 lg:grid-cols-5">
        {COMPARISON_METRICS.map(({ key, label }) => (
          <ComparisonCell key={key} label={label} current={current[key]} previous={previous[key]} />
        ))}
        <div className="bg-card p-4">
          <div className="font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Visit to signup</div>
          <div className="mt-2 text-sm font-semibold tabular-nums">{ratePct(currentSignupRate)}</div>
          <div className="mt-1 text-[11px] text-muted-foreground">
            {pointChange === null ? "Not comparable" : `${pointChange > 0 ? "+" : ""}${pointChange.toFixed(1)} pp`}
          </div>
        </div>
      </div>
    </section>
  );
}

function ComparisonCell({ label, current, previous }: { label: string; current: number; previous: number }) {
  const delta = current - previous;
  const relative = previous > 0 ? (delta / previous) * 100 : null;
  const Icon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : ArrowRight;
  const tone = delta > 0 ? "text-emerald" : delta < 0 ? "text-destructive" : "text-muted-foreground";
  return (
    <div className="bg-card p-4">
      <div className="font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
      <div className="mt-2 flex items-center gap-2">
        <span className="text-sm font-semibold tabular-nums">{current}</span>
        <Icon className={`h-3.5 w-3.5 ${tone}`} />
      </div>
      <div className={`mt-1 text-[11px] tabular-nums ${tone}`}>
        {delta > 0 ? "+" : ""}{delta} ({relative === null ? "Not comparable" : `${relative > 0 ? "+" : ""}${relative.toFixed(1)}%`})
      </div>
    </div>
  );
}

function Ga4Panel({ range }: { range: "7d" | "30d" | "all" }) {
  const fetchGa4 = useServerFn(getGa4Summary);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "ga4-summary", range],
    queryFn: () => fetchGa4({ data: { range } }),
    staleTime: 15 * 60_000,
  });
  const g = data?.data;
  const fmtTime = (s: number) => `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  const maxUsers = g?.countries[0]?.users ?? 0;
  return (
    <section className="space-y-4 border-t border-border/40 pt-8">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-gold">GA4 Telemetry</div>
        <h2 className="mt-1 font-display text-lg font-bold">Behavioral and geographic context</h2>
        <p className="text-xs text-muted-foreground">
          Google Analytics enrichment. Not the system of record. Refreshed every 15 minutes.
        </p>
      </div>
      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading GA4.
        </div>
      )}
      {data?.error && (
        <div className="rounded-md border border-border/60 bg-card p-4 text-xs text-muted-foreground">
          GA4 unavailable: {data.error}
        </div>
      )}
      {g && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <StatCard label="Sessions" value={g.sessions} />
            <StatCard label="Active users" value={g.active_users} />
            <StatCard label="New users" value={g.new_users} />
            <TextCard label="Engagement rate" value={`${(g.engagement_rate * 100).toFixed(1)}%`} />
            <TextCard label="Avg engagement" value={fmtTime(g.avg_engagement_s)} />
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="overflow-x-auto rounded-xl border border-border/60 bg-card lg:col-span-2">
              <table className="w-full text-sm">
                <thead className="bg-muted/30 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 text-left">Country</th>
                    <th className="px-4 py-3 text-right">Users</th>
                    <th className="px-4 py-3 text-right">Sessions</th>
                  </tr>
                </thead>
                <tbody>
                  {g.countries.length === 0 && (
                    <tr><td colSpan={3} className="px-4 py-6 text-center text-muted-foreground">No data for this period.</td></tr>
                  )}
                  {g.countries.map((c) => (
                    <tr key={c.country} className="border-t border-border/40">
                      <td className="px-4 py-2.5">
                        <div className="font-medium">{c.country}</div>
                        <div className="mt-1 h-1 rounded bg-muted/40">
                          <div className="h-1 rounded bg-gold" style={{ width: `${maxUsers ? (c.users / maxUsers) * 100 : 0}%` }} />
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{c.users}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{c.sessions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="rounded-xl border border-border/60 bg-card p-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Devices</div>
              <ul className="mt-3 space-y-2 text-sm">
                {g.devices.map((d) => (
                  <li key={d.device} className="flex justify-between capitalize">
                    <span>{d.device}</span>
                    <span className="tabular-nums">{pct(d.users, g.active_users)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function TextCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card p-4">
      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}
